import { useEffect, useRef, useState } from 'react';
import { TopBar } from './components/topbar';
import { Sidebar } from './components/sidebar';
import { WeekView } from './components/week-view';
import { DayView } from './components/day-view';
import { MonthView } from './components/month-view';
import { ActualDayView, ActualWeekView } from './components/actual-view';
import { HybridDayView, HybridWeekView } from './components/hybrid-view';
import { Modals } from './components/modals';
import { Toast } from './components/toast';
import { useUI } from './stores/ui';
import { useStream } from './hooks/useStream';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './lib/api';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { parseEst, snap15, type Task, type Block } from '@focus/shared';

type Chunk = { id: string; title: string; est: string; done: boolean; active?: boolean };

type DayDropData = {
  type: 'day';
  weekStart: number;
  dayIdx: number;
  hourH: number;
  startHour: number;
  endHour: number;
};

type TaskDropData = {
  type: 'task-target';
  taskId: string;
  placement: 'before' | 'after';
};

type GroupDropData = {
  type: 'group-target';
  tag: string;
  placement: 'before' | 'after';
};

type BlockDropData = {
  type: 'block';
  blockId: string;
  weekStart: number;
};

export function App() {
  const qc = useQueryClient();
  const { darkMode, view, viewMode, setToast, mobilePanel } = useUI();
  useStream();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useQuery({ queryKey: ['settings'], queryFn: api.getSettings });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [dragging, setDragging] = useState<{ title: string; estHours: number } | null>(null);
  const pointerPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointerPos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  const createBlock = useMutation({
    mutationFn: api.createBlock,
    onSuccess: (_d, vars: any) => {
      qc.invalidateQueries({ queryKey: ['blocks', vars.weekStart] });
      qc.invalidateQueries({ queryKey: ['blocks-month'] });
    },
  });
  const patchBlock = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Block> }) => api.patchBlock(id, patch),
    onSuccess: (_d, vars) => {
      // Invalidate for any week that could contain this block — simplest: refetch all blocks.
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: ['blocks-month'] });
      void vars;
    },
  });

  const reorderTasks = useMutation({
    mutationFn: api.reorderTasks,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const moveTaskTag = useMutation({
    mutationFn: ({ id, tag }: { id: string; tag: string }) => api.patchTask(id, { tag }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const moveChunk = useMutation({
    mutationFn: async ({
      sourceTaskId,
      targetTaskId,
      chunkId,
    }: {
      sourceTaskId: string;
      targetTaskId: string;
      chunkId: string;
    }) => {
      const all = qc.getQueryData<Task[]>(['tasks']) ?? [];
      const source = all.find((t) => t.id === sourceTaskId);
      const target = all.find((t) => t.id === targetTaskId);
      if (!source || !target) return;
      const sourceChunks: Chunk[] = Array.isArray((source as any).chunks) ? (source as any).chunks : [];
      const targetChunks: Chunk[] = Array.isArray((target as any).chunks) ? (target as any).chunks : [];
      const moving = sourceChunks.find((c) => c.id === chunkId);
      if (!moving) return;
      const nextSource = sourceChunks.filter((c) => c.id !== chunkId);
      const nextTarget = [...targetChunks, moving];
      await api.patchTask(sourceTaskId, { chunks: nextSource } as any);
      await api.patchTask(targetTaskId, { chunks: nextTarget } as any);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  function onDragStart(e: DragStartEvent) {
    const d = e.active.data.current as
      | { type: 'task'; task: Task }
      | { type: 'chunk'; sourceTaskId: string; chunkId: string; chunk: Chunk }
      | undefined;
    if (d?.type === 'task') {
      setDragging({ title: d.task.title, estHours: parseEst(d.task.est) });
    } else if (d?.type === 'chunk') {
      setDragging({ title: d.chunk.title, estHours: parseEst(d.chunk.est) });
    }
  }

  function onDragEnd(e: DragEndEvent) {
    setDragging(null);
    const active = e.active.data.current as
      | { type: 'task'; task: Task }
      | { type: 'group'; tag: string }
      | { type: 'chunk'; sourceTaskId: string; chunkId: string; chunk: Chunk }
      | undefined;
    const over = e.over;
    const overData = over?.data.current as
      | DayDropData
      | TaskDropData
      | GroupDropData
      | BlockDropData
      | undefined;
    if (!active || !overData) return;

    if (active.type === 'chunk') {
      if (overData.type !== 'task-target') return;
      if (overData.taskId === active.sourceTaskId) return;
      moveChunk.mutate({
        sourceTaskId: active.sourceTaskId,
        targetTaskId: overData.taskId,
        chunkId: active.chunkId,
      });
      setToast({ msg: `Chunk „${active.chunk.title}" verschoben`, tone: 'accent' });
      setTimeout(() => setToast(null), 1500);
      return;
    }

    if (active.type === 'group' && overData.type === 'group-target') {
      if (active.tag === overData.tag) return;
      const ui = useUI.getState();
      const cur = [...ui.tagOrder];
      const knownTags = new Set<string>();
      (qc.getQueryData<Task[]>(['tasks']) ?? [])
        .filter((t) => !t.done)
        .forEach((t) => knownTags.add(t.tag || 'Inbox'));
      for (const t of knownTags) if (!cur.includes(t)) cur.push(t);
      const withoutActive = cur.filter((t) => t !== active.tag);
      const targetIdx = withoutActive.indexOf(overData.tag);
      if (targetIdx < 0) return;
      withoutActive.splice(
        targetIdx + (overData.placement === 'after' ? 1 : 0),
        0,
        active.tag,
      );
      ui.setTagOrder(withoutActive);
      setToast({ msg: 'Themen-Reihenfolge geändert', tone: 'accent' });
      setTimeout(() => setToast(null), 1200);
      return;
    }

    if (active.type !== 'task') return;

    if (overData.type === 'block') {
      const all = (qc.getQueryData<Block[]>(['blocks', overData.weekStart]) ?? []);
      const blk = all.find((b) => b.id === overData.blockId);
      if (!blk) return;
      const contents = Array.isArray(blk.contents) ? blk.contents : [];
      if (contents.some((c) => c.taskId === active.task.id)) {
        setToast({ msg: 'Task ist bereits im Block', tone: 'accent' });
        setTimeout(() => setToast(null), 1200);
        return;
      }
      patchBlock.mutate({
        id: overData.blockId,
        patch: { contents: [...contents, { taskId: active.task.id, done: false }] } as Partial<Block>,
      });
      setToast({ msg: `„${active.task.title}" in Block übernommen`, tone: 'accent' });
      setTimeout(() => setToast(null), 1500);
      return;
    }

    if (overData.type === 'task-target') {
      if (overData.taskId === active.task.id) return;
      const all = qc.getQueryData<Task[]>(['tasks']) ?? [];
      const target = all.find((t) => t.id === overData.taskId);
      if (!target) return;
      const before = overData.placement === 'before';

      const open = all.filter((t) => !t.done);
      const byTag: Record<string, Task[]> = {};
      for (const t of open) {
        const g = t.tag || 'Inbox';
        if (!byTag[g]) byTag[g] = [];
        byTag[g].push(t);
      }
      for (const items of Object.values(byTag)) {
        items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      }
      const tags = Object.keys(byTag).sort((a, b) => {
        const minA = Math.min(...byTag[a].map((t) => t.order ?? Infinity));
        const minB = Math.min(...byTag[b].map((t) => t.order ?? Infinity));
        return minA - minB;
      });
      const sourceTagChanges = active.task.tag !== target.tag;
      const moved: Task = sourceTagChanges ? { ...active.task, tag: target.tag } : active.task;
      const flat: Task[] = [];
      for (const tag of tags) {
        for (const t of byTag[tag]) {
          if (t.id === active.task.id) continue;
          flat.push(t);
        }
      }
      const targetIdx = flat.findIndex((t) => t.id === target.id);
      if (targetIdx < 0) return;
      flat.splice(targetIdx + (before ? 0 : 1), 0, moved);

      if (sourceTagChanges) moveTaskTag.mutate({ id: active.task.id, tag: target.tag });
      reorderTasks.mutate(flat.map((t) => t.id));
      useUI.getState().setSort('manual');
      setToast({ msg: 'Reihenfolge geändert', tone: 'accent' });
      setTimeout(() => setToast(null), 1200);
      return;
    }

    if (overData.type !== 'day') return;

    const rect = (over?.rect as any);
    const pointerY = pointerPos.current.y;
    const relY = rect ? pointerY - rect.top : 0;
    const rawHours = overData.startHour + relY / overData.hourH;
    const s = Math.max(overData.startHour, Math.min(overData.endHour - 0.25, snap15(rawHours)));
    const duration = parseEst(active.task.est) || 0.5;
    const ee = Math.min(overData.endHour, s + Math.max(0.25, duration));

    createBlock.mutate({
      weekStart: overData.weekStart,
      dayIdx: overData.dayIdx,
      s,
      e: ee,
      t: active.task.title,
      cat: 'blockDeep',
      anchor: false,
      done: false,
      taskId: active.task.id,
      chunkId: null,
      source: 'user',
      gcalEventId: null,
      gcalCalendarId: null,
    } as any);
    setToast({ msg: `„${active.task.title}" verplant`, tone: 'accent' });
    setTimeout(() => setToast(null), 1500);
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="h-screen flex flex-col text-fg dark:text-dark-fg">
        <TopBar />
        <div className="flex-1 flex overflow-hidden">
          <div className={(mobilePanel === 'todos' ? 'block' : 'hidden') + ' md:block h-full'}>
            <Sidebar />
          </div>
          <main className={((mobilePanel === 'calendar' ? 'block' : 'hidden') + ' md:block flex-1 overflow-hidden')}>
            {view === 'week' && (
              viewMode === 'actual' ? <ActualWeekView /> :
              viewMode === 'hybrid' ? <HybridWeekView /> :
              <WeekView />
            )}
            {view === 'day' && (
              viewMode === 'actual' ? <ActualDayView /> :
              viewMode === 'hybrid' ? <HybridDayView /> :
              <DayView />
            )}
            {view === 'month' && <MonthView />}
          </main>
        </div>
        <Modals />
        <Toast />
      </div>
      <DragOverlay dropAnimation={null}>
        {dragging && (
          <div className="pointer-events-none rounded-md border border-accent bg-accentSoft px-2.5 py-1 text-[12px] font-medium shadow-pop">
            {dragging.title} · <span className="font-mono text-[10.5px]">{dragging.estHours.toFixed(2)} h</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
